import { useMemo } from 'react';

import { useLocation, useRouteLoaderData } from '@remix-run/react';

import { isSameSiteHref } from '~/components/DreamBuilder/nodes/Text/isSameSiteHref';
import type { RootLoaderData } from '~/root';

/**
 * Extracts the pathname from a URL string, handling both absolute and relative URLs.
 * Removes search params and hash fragments.
 */
const extractPathname = (href: string): string => {
  const trimmedHref = href.trim();

  if (!trimmedHref) return '/';

  try {
    const url = new URL(trimmedHref);
    return url.pathname;
  } catch {
    const pathname = trimmedHref.split('?')[0].split('#')[0];
    return pathname || '/';
  }
};

/**
 * Normalizes a pathname by removing trailing slashes.
 * Returns '/' for empty strings.
 */
const normalizePath = (pathname: string): string => {
  if (pathname === '' || pathname === '/') return '/';
  return pathname.replace(/\/$/, '');
};

/**
 * Determines if a link is active based on exact pathname match (excluding search params).
 * @param href - The href to check against the current location
 * @param isCustomLink - Whether the creator typed the href rather than picking a page
 * @returns Whether the link matches the current page
 */
export const useIsLinkActive = (href: string | undefined, isCustomLink?: boolean): boolean => {
  const location = useLocation();
  const { currentUrl } = useRouteLoaderData<RootLoaderData>('root') || {};

  return useMemo(() => {
    if (!href || /^(mailto|tel|javascript|data|file):/i.test(href)) return false;
    // Only typed links get a host check. Picked page links keep the publication's address from when
    // they were made, which can be its old beehiiv subdomain, so they match on path alone.
    if (isCustomLink && !isSameSiteHref(href.trim(), currentUrl)) return false;

    const linkPathname = extractPathname(href);
    const normalizedLinkPath = normalizePath(linkPathname);
    const normalizedCurrentPath = normalizePath(location.pathname);

    return normalizedLinkPath === normalizedCurrentPath;
  }, [href, isCustomLink, currentUrl, location.pathname]);
};
